/**
 * money.ts — the ONLY arithmetic and formatting path for money in this console.
 *
 * Money is integer paise everywhere: ₹3,399.00 is `339900`. Never a float, never a Number()
 * round-trip. Float arithmetic on money loses paise, and a lost paisa in a marketplace ledger is
 * a reconciliation break somebody has to chase through a month of transactions.
 *
 * The console never computes a business amount. It displays what the API resolved and posts what
 * the API asked for — commissions, taxes, refund quotas and payout splits are the backend's,
 * priced against a pinned price_version. These helpers only *render* and *total* what it sent.
 *
 * Rendering rules (brand doc 01 §7, design system readme):
 *   ₹ prefix, never "Rs." and never "INR"
 *   Indian grouping — ₹1,23,456, not ₹123,456
 *   tabular numerals so columns line up for scanning
 *   paise shown only when non-zero, and always in the money modules
 *   deductions carry an explicit − (U+2212 minus, not a hyphen — it aligns with digits)
 */

/** U+2212. A hyphen is narrower than a digit and makes a money column ragged. */
const MINUS = '−';

/**
 * Indian digit grouping: the last three digits, then twos.
 * 1234 → 1,234 · 123456 → 1,23,456 · 12345678 → 1,23,45,678
 */
export function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3);
  const groups: string[] = [];
  let i = rest.length;
  while (i > 2) {
    groups.unshift(rest.slice(i - 2, i));
    i -= 2;
  }
  if (i > 0) groups.unshift(rest.slice(0, i));
  return `${groups.join(',')},${last3}`;
}

export interface FormatOptions {
  /** Force the paise decimals even when they are zero. The money modules always do. */
  paise?: boolean;
  /** Drop the ₹. For a column whose header already carries the unit. */
  bare?: boolean;
}

/**
 * Format integer paise as rupees.
 * formatInr(339900) → '₹3,399'   ·   formatInr(339900, { paise: true }) → '₹3,399.00'
 * formatInr(-137475) → '−₹1,374.75'
 */
export function formatInr(paise: number, opts: FormatOptions = {}): string {
  if (!Number.isFinite(paise)) throw new RangeError(`formatInr: not a finite number: ${paise}`);
  if (!Number.isInteger(paise)) {
    throw new RangeError(
      `formatInr: money must be integer paise, got ${paise}. ` +
        'A fractional paisa means someone did float arithmetic upstream.',
    );
  }
  const negative = paise < 0;
  const abs = Math.abs(paise);
  const rupees = Math.trunc(abs / 100);
  const fraction = abs % 100;

  const whole = groupIndian(String(rupees));
  const decimals = fraction !== 0 || opts.paise ? `.${String(fraction).padStart(2, '0')}` : '';
  const symbol = opts.bare ? '' : '₹';
  return `${negative ? MINUS : ''}${symbol}${whole}${decimals}`;
}

/**
 * Compact Indian form for stat tiles, where the exact paisa is noise.
 * Lakh and crore, because a ₹ figure written as "1.2M" reads as a foreign currency to the
 * audience this console serves.
 */
export function formatInrCompact(paise: number): string {
  if (!Number.isInteger(paise)) throw new RangeError(`formatInrCompact: expected integer paise, got ${paise}`);
  const negative = paise < 0;
  const rupees = Math.trunc(Math.abs(paise) / 100);
  const sign = negative ? MINUS : '';

  const unit = (value: number, suffix: string): string => {
    const scaled = Math.round(value * 10) / 10;
    const text = Number.isInteger(scaled) ? String(scaled) : scaled.toFixed(1);
    return `${sign}₹${text} ${suffix}`;
  };

  if (rupees >= 10_000_000) return unit(rupees / 10_000_000, 'Cr');
  if (rupees >= 100_000) return unit(rupees / 100_000, 'L');
  return `${sign}₹${groupIndian(String(rupees))}`;
}

/** Total a list of paise. Integer addition, so it cannot drift. */
export function sumPaise(values: readonly number[]): number {
  let total = 0;
  for (const v of values) {
    if (!Number.isInteger(v)) throw new RangeError(`sumPaise: expected integer paise, got ${v}`);
    total += v;
  }
  return total;
}

/**
 * Parse a rupee string the operator typed into integer paise, reading digit by digit.
 *
 * Deliberately NOT parseFloat: parseFloat('3399.10') * 100 is 339909.99999999994, which floors
 * to a paisa short. Reading the characters avoids binary floating point entirely.
 *
 * Accepts '3399', '3,399', '₹3,399.10', '-3399.5' (→ 3399.50). Returns null on anything else —
 * the caller decides whether that is a validation error or an empty field.
 */
export function parsePaise(input: string): number | null {
  const raw = input.trim();
  if (raw === '') return null;

  let negative = false;
  let rest = raw;
  if (rest.startsWith(MINUS) || rest.startsWith('-')) {
    negative = true;
    rest = rest.slice(1);
  }
  if (rest.startsWith('₹')) rest = rest.slice(1);
  rest = rest.replace(/,/g, '').trim();
  if (rest === '') return null;

  const parts = rest.split('.');
  if (parts.length > 2) return null;
  const [whole = '', fraction = ''] = parts;
  if (!/^\d+$/.test(whole)) return null;
  if (fraction !== '' && !/^\d{1,2}$/.test(fraction)) return null;

  let rupees = 0;
  for (const ch of whole) rupees = rupees * 10 + (ch.charCodeAt(0) - 48);

  const padded = fraction.padEnd(2, '0');
  const fractionPaise = (padded.charCodeAt(0) - 48) * 10 + (padded.charCodeAt(1) - 48);

  const paise = rupees * 100 + fractionPaise;
  return negative ? -paise : paise;
}
